/* eslint-disable max-depth */
import React, { useState } from "react";
import { connect, useDispatch } from "react-redux";
import withReduxProvider from "web/react/redux-provider";
import GenericModal from "web/react/components/generic-modal/generic-modal";
import { closeCustomerCaptureOverlay } from "web/redux/ducks/customer-capture-overlay";
import environment from "web/script/modules/environment";
import SendToApp from "web/react/components/send-to-app/send-to-app";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import EmailCaptureOverlay from "web/react/components/email-capture-overlay/email-capture-overlay";
import { RawBranchLink } from "web/script/utils/branch-io";
import styles from "./email-app-capture-overlay.css";

function mapStateToProps(state): object {
    const { captureType, open, productImageUrl } = state.customerCaptureOverlayReducer;
    return {
        captureType,
        overlayOpen: open,
        productImageUrl,
    };
}
interface TrackedLinkProps {
    leadUrl: string;
    productId: string;
    reason: string;
}

interface EmailAppCaptureOverlayProps {
    paywalledClassName?: string | null;
    appDeepLinkURL?: URL;
    trackedLink?: TrackedLinkProps;
    financialIncentiveWebviewToken?: string;
    downloadCTA?: string;
    captureType: string;
    overlayOpen: boolean;
    productImageUrl?: string;
}

export function EmailAppCaptureOverlay({
    downloadCTA,
    captureType,
    overlayOpen,
    productImageUrl,
    ...props
}: EmailAppCaptureOverlayProps): JSX.Element {
    let branchLink;
    let title;
    let hidden = "hidden";
    let visibilityChange = "visibilitychange";
    downloadCTA = downloadCTA ? downloadCTA : gettext("feed.show_more.app_overlay.cta");
    let analyticsEventLabel = "is-product-shop-now";
    const pageType = environment.get("pageSubType");

    const dispatch = useDispatch();
    const [showApp, setShowApp] = useState(true);

    function handleVisibilityChange(): void {
        if (!document[hidden]) {
            analytics.event(captureType, "view_form", "back_overlay");
            document.removeEventListener(visibilityChange, handleVisibilityChange);
        }
    }

    function triggerAfterOpen(): void {
        analytics.event(captureType, "open_signup_form", "open_overlay", true);
        document.addEventListener(visibilityChange, handleVisibilityChange, false);
    }

    function triggerClose(): boolean | void {
        analytics.event(captureType, "close_signup_form", "exit_signup");
        document.removeEventListener(visibilityChange, handleVisibilityChange);
        dispatch(closeCustomerCaptureOverlay());
    }

    if (captureType === "signup_pdp_save" && pageType === "out_of_stock") {
        // Back In Stock
        title = gettext("saved_items.sign_up_save_item_title_option_b");
        branchLink = RawBranchLink.AppSignupBIS;
        analyticsEventLabel = "signup_touchpoint_BIS";
    } else if (captureType === "signup_du") {
        // Designer Updates
        title = gettext("account.register.return_lead_title_follow_products_designer");
        branchLink = RawBranchLink.AppSignupDU;
        analyticsEventLabel = "signup_touchpoint_DU";
    } else {
        // Save For Later
        title = gettext("saved_items.sign_up_save_item_title_option_b");
        branchLink = RawBranchLink.AppSignupS4L;
        analyticsEventLabel = "signup_touchpoint_S4L";
    }

    function triggerChange(): void {
        analytics.event(captureType, "change_signup_form_type", "change_overlay");
        setShowApp(!showApp);
    }

    return (
        <GenericModal
            isOpen={overlayOpen}
            onAfterOpen={triggerAfterOpen}
            onClose={triggerClose}
            className={styles.modal}
        >
            <>
                <h1 className={styles.title}>{title}</h1>
                {showApp && (
                    <>
                        <SendToApp
                            analyticsEventLabel={analyticsEventLabel}
                            url={branchLink}
                            downloadCTA={downloadCTA}
                            {...props}
                            productImageURL={productImageUrl}
                        />

                        <div className={styles.separator}>
                            <div className={styles.separatorLine}></div>
                            <span className={styles.separatorText}>
                                {gettext("account.register.sign_up_by_email.label")}
                            </span>
                            <div className={styles.separatorLine}></div>
                        </div>
                    </>
                )}
                <div className={styles.ellipse} onClick={triggerChange}>
                    {showApp ? (
                        <SVGIcon className={styles.chevronDown} name={"chevron-thin"} />
                    ) : (
                        <SVGIcon className={styles.chevronUp} name={"chevron-thin"} />
                    )}
                </div>
                <div className={styles.cover}></div>
                <div className={styles.emailFormHolder}>
                    <EmailCaptureOverlay contentOnly={true} autoFocus={false} {...props} />
                </div>
            </>
        </GenericModal>
    );
}
export default withReduxProvider(connect(mapStateToProps)(EmailAppCaptureOverlay));
